import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  AssessmentRunOptions,
  CompanyRegistrationPayload,
  OpsDecisionPayload,
  PreApplication,
  PreApplicationDocument,
  PreApplicationOps,
  PreApplicationOpsListResponse,
  PreApplicationRecommendationResult,
  PreApplicationReviewStatus,
  ModelRuns,
} from './pre-application.types';

/**
 * Client for the pre-application (guided intake) API. Mirrors the KS/DF service
 * pattern: injects HttpClient directly and builds its base off environment.apiUrl.
 */
@Injectable({ providedIn: 'root' })
export class PreApplicationService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/api/applications`;

  /**
   * Start (or reuse the entity's open) pre-application.
   *
   * `aiProcessingConsent` carries the client's POPIA consent to AI-assisted
   * assessment. This call RECORDS a consent it is given but does not refuse one
   * it is not — creating an intake sends nothing abroad. The refusal that
   * matters is on `uploadDocument`, which returns `AI_CONSENT_REQUIRED` while no
   * active consent stands. Use `recordAiConsent` when the intake already exists.
   *
   * `startNew` opens a new record even when an editable one exists.
   */
  create(
    entityShortId: string,
    dfIntent = false,
    aiProcessingConsent = false,
    startNew = false,
  ): Observable<PreApplication> {
    return this.http.post<PreApplication>(`${this.base}/pre/`, {
      entity: entityShortId,
      df_intent: dfIntent,
      ai_processing_consent: aiProcessingConsent,
      start_new: startNew,
    });
  }

  get(shortId: string): Observable<PreApplication> {
    return this.http.get<PreApplication>(`${this.base}/pre/${shortId}/`);
  }

  /**
   * The entity's current pre-application in *any* review state (incl. SUBMITTED
   * / PROCEEDED, which `create()` won't return), or `null` if none exists yet.
   * Used to render the review-lifecycle state on page load without a short_id.
   */
  current(entityShortId: string): Observable<PreApplication | null> {
    return this.http
      .get<PreApplication | Record<string, never>>(`${this.base}/pre/`, {
        params: { entity: entityShortId },
      })
      .pipe(map((res) => ('short_id' in res ? (res as PreApplication) : null)));
  }

  /**
   * The door's outcome for an ENTITY, before any record exists (§A1.4). Read
   * on load so a business the rule refers — a trust, an NPO, no CIPC
   * registration — is told so before it is shown document steps, and before
   * any upload would run an AI check on it. Side-effect free on the server.
   */
  door(entityShortId: string): Observable<PreApplicationRecommendationResult> {
    return this.http.get<PreApplicationRecommendationResult>(`${this.base}/pre/door/`, {
      params: { entity: entityShortId },
    });
  }

  /** Update the DF-intent answer and re-evaluate the recommendation. */
  setDfIntent(shortId: string, dfIntent: boolean): Observable<PreApplication> {
    return this.http.patch<PreApplication>(`${this.base}/pre/${shortId}/`, {
      df_intent: dfIntent,
    });
  }

  /**
   * Record the AI-processing consent on an intake that already exists — the
   * client ticked the box after the record was created (e.g. on the second
   * upload step). Append-only: the server adds a row only when no active
   * consent stands, so calling this twice is harmless.
   *
   * The counterpart is `withdrawAiConsent` below (FLOW-290). Re-consent after
   * a withdrawal comes back through here and writes a fresh row — the withdrawn
   * one is never revived.
   */
  recordAiConsent(shortId: string): Observable<PreApplication> {
    return this.http.patch<PreApplication>(`${this.base}/pre/${shortId}/`, {
      ai_processing_consent: true,
    });
  }

  /**
   * Withdraw the AI-processing consent on this intake (FLOW-290, POPIA s11(2)).
   *
   * The act behind the "You can withdraw consent at any time" promise. Not an
   * update of the consent row: the server transitions it and every AI gate
   * reads the result live, so the next assessment is refused immediately.
   * Re-consenting goes back through the existing consent path — it writes a new
   * row rather than reviving this one. A second withdrawal is a 404
   * `NO_ACTIVE_CONSENT`.
   *
   * `reason` is optional and never shown back: the right is unqualified, so the
   * UI does not ask a client to justify it. Ops passes one when keying a
   * withdrawal received by phone or email.
   */
  withdrawAiConsent(shortId: string, reason?: string): Observable<PreApplication> {
    return this.http.post<PreApplication>(
      `${this.base}/pre/${shortId}/ai-consent/withdraw/`,
      reason ? { reason } : {},
    );
  }

  uploadDocument(
    shortId: string,
    file: File,
    docType: string,
    label?: string,
  ): Observable<PreApplicationDocument> {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('doc_type', docType);
    if (label) {
      fd.append('label', label);
    }
    return this.http.post<PreApplicationDocument>(`${this.base}/pre/${shortId}/documents/`, fd);
  }

  /**
   * Attribute an uploaded bank statement to one of the business's DECLARED
   * accounts (FLOW-243). `null` clears an attribution filed against the wrong
   * account. The server refuses an account nobody has declared — attributing to
   * an undeclared account would put it into the declared list sideways.
   */
  attributeDocument(
    shortId: string,
    docShortId: string,
    accountShortId: string | null,
  ): Observable<PreApplicationDocument> {
    return this.http.patch<PreApplicationDocument>(
      `${this.base}/pre/${shortId}/documents/${docShortId}/`,
      { source_account_ref: accountShortId },
    );
  }

  deleteDocument(shortId: string, docShortId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/pre/${shortId}/documents/${docShortId}/`);
  }

  /** The N4-safe outcome (headline + reason + which product CTAs are live). */
  recommendation(shortId: string): Observable<PreApplicationRecommendationResult> {
    return this.http.get<PreApplicationRecommendationResult>(
      `${this.base}/pre/${shortId}/recommendation/`,
    );
  }

  /**
   * Submit the pre-application for a CapX decision. The client sends their
   * details/documents and waits — they don't pick a product.
   */
  submit(shortId: string): Observable<PreApplication> {
    return this.http.post<PreApplication>(`${this.base}/pre/${shortId}/submit/`, {});
  }

  // -------------------------------------------------------------------------
  // Ops worklist (FLOW-149). Authorised Ops only — the backend enforces it; the
  // opsGuard keeps the routes off everyone else's UI.
  // -------------------------------------------------------------------------

  /** The Ops worklist. Ops discovers work here — there is no per-submission email. */
  opsList(query?: {
    review_status?: PreApplicationReviewStatus;
    ordering?: 'submitted_at' | '-submitted_at';
  }): Observable<PreApplicationOpsListResponse> {
    let params: Record<string, string> = {};
    if (query?.review_status) {
      params = { ...params, review_status: query.review_status };
    }
    if (query?.ordering) {
      params = { ...params, ordering: query.ordering };
    }
    return this.http.get<PreApplicationOpsListResponse>(`${this.base}/ops/pre/`, { params });
  }

  /** The reviewer's full view: documents, internal signal, history, advisory flag. */
  opsGet(shortId: string): Observable<PreApplicationOps> {
    return this.http.get<PreApplicationOps>(`${this.base}/ops/pre/${shortId}/`);
  }

  /** FLOW-330 — every bank statement as one .zip, built server-side; each recorded. */
  opsStatementsZip(shortId: string): Observable<Blob> {
    return this.http.get(`${this.base}/ops/pre/${shortId}/bank-statements.zip`, {
      responseType: 'blob',
    });
  }

  /** Record the decision. A 409 means the record moved on since it was opened. */
  opsDecision(shortId: string, payload: OpsDecisionPayload): Observable<PreApplicationOps> {
    return this.http.post<PreApplicationOps>(`${this.base}/ops/pre/${shortId}/decision/`, payload);
  }

  /** Every model's run of the assessment, each read as the file would read it. */
  modelRuns(shortId: string): Observable<ModelRuns> {
    return this.http.get<ModelRuns>(`${this.base}/ops/pre/${shortId}/model-runs/`);
  }

  /**
   * Compute an indicative Kickstart figure for this intake (FLOW-224 part 1).
   *
   * Ops-only, and there is deliberately no client-facing counterpart: the figure
   * may not be shown to a client while the reproducibility gate is failed. The
   * figure is computed against a Kickstart DRAFT created for the purpose — the
   * rules engine never reads the pre-application record.
   *
   * Returns the reviewer's full record, with the run on `indicative_sizing`, so
   * there is nothing to poll for. A 409 carries a code: `AI_CONSENT_REQUIRED`,
   * `NO_BANK_STATEMENT`, or `SIZING_IN_FLIGHT`.
   *
   * `area`: ask only that area of the latest run again, the other four held
   * (refused `NO_AREA_RUN` where there is no area run to hold them from).
   */
  opsRequestIndicativeSizing(
    shortId: string,
    model = '',
    area = '',
  ): Observable<PreApplicationOps> {
    return this.http.post<PreApplicationOps>(`${this.base}/ops/pre/${shortId}/indicative-sizing/`, {
      ...(model ? { model } : {}),
      ...(area ? { area } : {}),
    });
  }

  /** What this person may press: the models on offer and their runs left. */
  opsRunOptions(shortId: string): Observable<AssessmentRunOptions> {
    return this.http.get<AssessmentRunOptions>(
      `${this.base}/ops/pre/${shortId}/indicative-sizing/`,
    );
  }

  /**
   * FLOW-397 — *Company registration — capture first* (DOC-91 §5). Multipart:
   * only the fields given are sent, so nothing reads as pre-filled. The
   * response is the whole record: the card, the decision block, the indicative
   * card and History all re-read from it.
   */
  opsCaptureCompanyRegistration(
    shortId: string,
    payload: CompanyRegistrationPayload,
  ): Observable<PreApplicationOps> {
    const form = new FormData();
    for (const [key, value] of Object.entries(payload)) {
      if (value === undefined || value === null || value === '') continue;
      form.append(key, value as string | Blob);
    }
    return this.http.post<PreApplicationOps>(
      `${this.base}/ops/pre/${shortId}/company-registration/`,
      form,
    );
  }

  /** DOC-93 §2 — *Fetch again from CIPC*: a new entry, the earlier one stays. */
  opsFetchFromCipc(shortId: string): Observable<PreApplicationOps> {
    return this.http.post<PreApplicationOps>(
      `${this.base}/ops/pre/${shortId}/company-registration/fetch/`,
      {},
    );
  }

  /** DOC-93 §5 — the status corrected from a CIPC document the client supplied. */
  opsCorrectCipcStatus(
    shortId: string,
    cipcStatus: string,
    file: File,
  ): Observable<PreApplicationOps> {
    const form = new FormData();
    form.append('cipc_status', cipcStatus);
    form.append('file', file);
    return this.http.post<PreApplicationOps>(
      `${this.base}/ops/pre/${shortId}/company-registration/correct-status/`,
      form,
    );
  }

  /** FLOW-397 — *No CIPC record found*, with a reason: the referral opens Decline only. */
  opsRecordNoCipcRecord(shortId: string, reason: string): Observable<PreApplicationOps> {
    return this.http.post<PreApplicationOps>(
      `${this.base}/ops/pre/${shortId}/company-registration/no-record/`,
      { reason },
    );
  }
}
